import * as Immutable from 'immutable';
import { CoordinatePoint } from 'types/point';
import { Precision } from 'types/precision';

/**
 * find non-closed figure with first or last point
 */
export const findAlignedPoints = <T extends CoordinatePoint>(
  wallPointsList: Immutable.List<T>[], targetPoint: T, snappedTarget: T, precision: number
): T[] => {
  // The found Alignments in the 4 Cardinal Directions;
  let alignedTop: T;
  let alignedLeft: T;
  let alignedBottom: T;
  let alignedRight: T;

  // If there are no walls/points, do not check for alignment.
  if (!wallPointsList || wallPointsList.length < 1) return [];

  // To catch and prevent drawing alignments to the previous wall once drawn, get the Last Wall
  // We cannot trust that the pointsList will be in order; if the targetPoint matches the lastEndPoint, we have found it.
  const lastWall = wallPointsList.find(wallPoints =>
    wallPoints.find(x => arePointsAxisAligned(x.x, targetPoint.x, precision) && arePointsAxisAligned(x.y, targetPoint.y, precision)));

  // const lastEndPoint = (lastWall) ? lastWall.get(1) : null;// the point that matches the targetPoint
  const lastStartPoint = (lastWall) ? lastWall.get(0) : null; // the point we do not want to align with.

  // Check every point in every wall to see if they are in alignment with the target.
  wallPointsList.forEach((wallPoints) => {
    wallPoints.forEach((point) => {
      if (arePointsAxisAligned(targetPoint.x, point.x, precision)) {
        if (point.y < targetPoint.y && (!alignedTop || alignedTop.y < point.y)) {
          alignedTop = point;
        } else if (point.y > targetPoint.y && (!alignedBottom || alignedBottom.y > point.y)) {
          alignedBottom = point;
        }
      } else if (arePointsAxisAligned(targetPoint.y, point.y, precision)) {
        if (point.x < targetPoint.x && (!alignedLeft || alignedLeft.x < point.x)) {
          alignedLeft = point;
        } else if (point.x > targetPoint.x && (!alignedRight || alignedRight.x > point.x)) {
          alignedRight = point;
        }
      }
    });
  });

  const alignedPoints = [alignedTop!, alignedRight!, alignedBottom!, alignedLeft!].filter(x => x);

  // Try to fix alignment with the last placed point by removing it from found alignments.
  if (lastStartPoint) {
    const previousPointIndex = alignedPoints.findIndex(x => x.pointId === lastStartPoint!.pointId);
    if (previousPointIndex > -1) {
      alignedPoints.splice(previousPointIndex, 1);
    }
  }
  return alignedPoints;
};

export const findAlignedPointsToSelection = <T extends Point | CoordinatePoint>(
  allWallPointsList: Immutable.List<T>[], targetObject: Immutable.List<T>,
): Immutable.List<Point | CoordinatePoint>[] => {
  // filter out all non-point objects from selection. Return empty if there are no points to align/with.
  const selectedPoints = targetObject.filter(x => (x as Point).pointType === undefined);

  if (allWallPointsList.length === 0 || selectedPoints.count() === 0) return [];

  let finalAligns: Immutable.List<CoordinatePoint>[] = [];
  let finalPoints: CoordinatePoint[] = [];
  const previousPositions: string[] = [];
  const blackListedPoints: string[] = [];

  // find the list of points we do not want to check alignments with
  // the points on the other side of walls that are selected, to avoid double alignments
  selectedPoints.forEach(point => {
    if (!previousPositions.includes(point.pointId)) previousPositions.push(point.pointId);
    allWallPointsList.forEach(wall => {
      const firstWallId = wall.first()!.pointId;
      const lastWallId = wall.last()!.pointId;
      if (point.pointId === firstWallId) {
        if (!blackListedPoints.includes(lastWallId)) blackListedPoints.push(lastWallId);
      }
      if (point.pointId === lastWallId) {
        if (!blackListedPoints.includes(firstWallId)) blackListedPoints.push(firstWallId);
      }
    });
  });

  // OUTER LOOP: Check for alignments for each selected Point.
  selectedPoints.forEach(targetPoint => {
    let alignedTop: CoordinatePoint;
    let alignedLeft: CoordinatePoint;
    let alignedBottom: CoordinatePoint;
    let alignedRight: CoordinatePoint;

    // Find the lists of other selection points on the same horizontal/vertical axis.
    const sharedY = selectedPoints.filter(p => p.y === targetPoint.y);
    const sharedX = selectedPoints.filter(p => p.x === targetPoint.x);

    // To avoid alignment lines crossing the selection, only align the points that are the furthest in that direction on their specific axis's
    const topMost = !sharedX.some(s => s.y < targetPoint.y);
    const bottomMost = !sharedX.some(s => s.y > targetPoint.y);
    const leftMost = !sharedY.some(s => s.x < targetPoint.x);
    const rightMost = !sharedY.some(s => s.x > targetPoint.x);

    allWallPointsList.forEach(wall => {
      wall.forEach(point => {
        let makeInvisible = false;
        // We do not want to align the new position with the previous position that is not visually there.
        if (previousPositions.includes(point.pointId)) return;
        if (blackListedPoints.includes(point.pointId)) makeInvisible = true;

        // If Horizontally Aligned, Test for Up/Down Alignments
        if (point.x === targetPoint.x) {
          // Check if points above selection are aligned
          if (topMost && point.y < targetPoint.y) {
            if (!alignedTop || alignedTop.y < point.y) {
              alignedTop = { ...point };
              if (makeInvisible) alignedTop.tag = 'hideAlignment';
            }
          // Check if points below selection are aligned
          } else if (bottomMost && point.y > targetPoint.y) {
            if (!alignedBottom || alignedBottom.y > point.y) {
              alignedBottom = { ...point };
              if (makeInvisible) alignedBottom.tag = 'hideAlignment';
            }
          }
        // If Vertically Aligned, Test for Left/Right Alignments
        } else if (point.y === targetPoint.y) {
          if (rightMost && point.x > targetPoint.x) {
            if (!alignedRight || point.x < alignedRight.x) {
              alignedRight = { ...point };
              if (makeInvisible) alignedRight.tag = 'hideAlignment';
            }
          } else if (leftMost && point.x < targetPoint.x) {
            if (!alignedLeft || point.x > alignedLeft.x) {
              alignedLeft = { ...point };
              if (makeInvisible) alignedLeft.tag = 'hideAlignment';
            }
          }
        }
      }); // END POINT LOOP
    }); // END WALL LOOP

    // assemble list of found alignments, removing nulls, and points previously found to be in alignment.
    const alignedPoints : (CoordinatePoint)[] = [
      alignedTop!,
      alignedRight!,
      alignedBottom!,
      alignedLeft!
    ].filter(x => x && !finalPoints.includes(x));

    finalPoints = finalPoints.concat(alignedPoints);
    finalAligns = finalAligns.concat(alignedPoints.map(alignedPoint => (Immutable.List<Point | CoordinatePoint>([alignedPoint, targetPoint]))));
  }); // END TARGET LOOP

  return finalAligns;
};

/**
 * find non-closed figure with first or last point
 */
export const findClosestAlignmentPointInDirection = <T extends CoordinatePoint>(
  wallPointsList: Immutable.List<T>[], startPoint: CoordinatePoint, endPoint: CoordinatePoint, direction: string
): CoordinatePoint => {
  // first, prepare shorthand references to the direction so we have two variables to check, instead of 4 values.
  const xAxis = (direction.toUpperCase() === 'LEFT' || direction.toUpperCase() === 'RIGHT');
  const yAxis = !xAxis;
  const positive = (direction.toUpperCase() === 'UP' || direction.toUpperCase() === 'RIGHT');
  const negative = !positive;

  // Then, prepare the list
  const initialStartVal = Math.round((xAxis) ? startPoint.x : startPoint.y);
  const initialEndVal = Math.round((xAxis) ? endPoint.x : endPoint.y);
  const initialList = (initialStartVal === initialEndVal) ? [initialStartVal] : [initialStartVal, initialEndVal];

  const pointsList = createOtherPointsCoordinateList(wallPointsList, initialList, [], xAxis);
  pointsList.sort((a, b) => a - b);

  // get the position of the origin, and get the next one in the direction we want.
  const originIndex = pointsList.indexOf(initialEndVal);
  let nextIndex = 0;
  if (xAxis) { // X axis straightforwardly increases as going right
    nextIndex = (positive) ? originIndex + 1 : originIndex - 1;
  } else { // Y axis inversely increases as going down
    nextIndex = (negative) ? originIndex + 1 : originIndex - 1;
  }

  // if an invalid index, the originPoint is the furthest point in that direction, so just return the original point
  if (nextIndex === -1 || nextIndex === pointsList.length) {
    return endPoint;
  }

  const alignmentPoint = { x: (yAxis) ? endPoint.x : pointsList[nextIndex], y: (xAxis) ? endPoint.y : pointsList[nextIndex] };
  return alignmentPoint;
};


/**
 * find non-closed figure with first or last point
 */
export const findClosestAlignmentPointInDirectionForSelection = <T extends CoordinatePoint>(
  wallPointsList: Immutable.List<T>[], guidePoint: CoordinatePoint, selection: CoordinatePoint[], direction: string
): CoordinatePoint => {
  // first, prepare shorthand references to the direction so we have two variables to check, instead of 4 values.
  const xAxis = (direction.toUpperCase() === 'LEFT' || direction.toUpperCase() === 'RIGHT');
  const positive = (direction.toUpperCase() === 'UP' || direction.toUpperCase() === 'RIGHT');
  const negative = !positive;

  // Then, prepare the list
  const pointsList = createOtherPointsCoordinateList(wallPointsList, [], selection, xAxis);
  pointsList.sort((a, b) => a - b);

  let secondaryGuide = structuredClone(guidePoint);
  let jumpCoordinate = (xAxis) ? guidePoint.x : guidePoint.y;
  let jumpDistance = 0;

  let pointAdded = false;
  selection.forEach((selectedPoint) => {
    // Prepare by adding the selectedPoint to the pointsList
    const selectedPointValue = (xAxis) ? selectedPoint.x : selectedPoint.y;
    if (!pointsList.includes(selectedPointValue)) {
      pointAdded = true;
      pointsList.push(selectedPointValue);
    }
    pointsList.sort((a, b) => a - b);

    const selectedIndex = pointsList.indexOf(selectedPointValue);

    let nextIndex = 0;
    if (xAxis) { // X axis straightforwardly increases as going right
      nextIndex = (positive) ? selectedIndex + 1 : selectedIndex - 1;
    } else { // Y axis inversely increases as going down
      nextIndex = (negative) ? selectedIndex + 1 : selectedIndex - 1;
    }

    // if the next index is within the array...
    if (nextIndex >= 0 && nextIndex <= pointsList.length - 1) {
      const nextPointValue = pointsList[nextIndex];
      const newDistance = Math.abs(Number(selectedPointValue) - Number(nextPointValue));
      if (jumpDistance === 0 || (newDistance >= 1 && newDistance < jumpDistance)) {
        secondaryGuide = selectedPoint;
        jumpCoordinate = nextPointValue;
        jumpDistance = newDistance;
      }
    }

    // Cleanup by removing currentPoint from the pointsList;
    if (pointAdded) {
      pointAdded = false;
      pointsList.splice(selectedIndex, 1);
    }
  });

  // if there is no jump, return the guide point
  if (jumpDistance === 0) {
    return guidePoint;
  }

  // if the guidePoint is jumping...
  const alignmentPoint = structuredClone(guidePoint);
  if (secondaryGuide.x === guidePoint.x && secondaryGuide.y === guidePoint.y) {
    if (xAxis) {
      alignmentPoint.x = Number(jumpCoordinate);
    } else { // if yAxis
      alignmentPoint.y = Number(jumpCoordinate);
    }
    return alignmentPoint;
  }

  let d: number;
  if (xAxis) {
    d = secondaryGuide.x - Number(jumpCoordinate);
  } else { // if yAxis
    d = secondaryGuide.y - Number(jumpCoordinate);
  }

  if (xAxis) {
    alignmentPoint.x -= d;
  } else { // if yAxis
    alignmentPoint.y -= d;
  }

  return alignmentPoint;
};

// Utility Function to create a list of Unique Alignment Coordiantes for the given Axis.
const createOtherPointsCoordinateList = <T extends CoordinatePoint>(
  wallPointsList: Immutable.List<T>[], initialList: string[], blackList: CoordinatePoint[], isXAxis: boolean
): string[] => {
  wallPointsList.forEach((wallPoints) => {
    wallPoints.forEach((currentPoint) => {
      // do not include the other points in the selection as potential Alignment Points
      if (blackList.includes(currentPoint)) return;

      // check each point to see if they add a unique value, and if so add it to the list.
      const newVal = Math.round((isXAxis) ? currentPoint.x : currentPoint.y);
      if (!initialList.includes(newVal)) {
        initialList.push(newVal);
      }
    });
  });
  return initialList;
};

const arePointsAxisAligned = (main: number, checked: number, precision: number): boolean => {
  // the number of units in a cell between the current precision.
  const offset = Precision.gridSize / precision;

  // if the main point is within a 1/2 of the distance in either direction, they are aligned
  if (Math.abs(main - checked) < (offset / 2)) return true;
  return false;
};
